import Button from "../../ui/Button/Button";
import AdminTableFiltersCard from "../ui/AdminTableFiltersCard";

type AdminGigsFiltersProps = {
  contentType: "GIGS" | "BUSINESS_LISTINGS";
  search: string;
  poster: string;
  status: string;
  createdFromDate: string;
  createdToDate: string;
  startFromDate: string;
  startToDate: string;
  pageSize: number;
  onSearchChange: (value: string) => void;
  onPosterChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onCreatedFromDateChange: (value: string) => void;
  onCreatedToDateChange: (value: string) => void;
  onStartFromDateChange: (value: string) => void;
  onStartToDateChange: (value: string) => void;
  onPageSizeChange: (value: number) => void;
  onRequestTotalCount: () => void;
  onContentTypeChange: (value: "GIGS" | "BUSINESS_LISTINGS") => void;
};

const AdminGigsFilters = ({
  contentType,
  search,
  poster,
  status,
  createdFromDate,
  createdToDate,
  startFromDate,
  startToDate,
  pageSize,
  onSearchChange,
  onPosterChange,
  onStatusChange,
  onCreatedFromDateChange,
  onCreatedToDateChange,
  onStartFromDateChange,
  onStartToDateChange,
  onPageSizeChange,
  onRequestTotalCount,
  onContentTypeChange,
}: AdminGigsFiltersProps) => {
  return (
    <AdminTableFiltersCard>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[150px] flex-[0_1_170px]">
          <label htmlFor="admin-gigs-filter-type" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Type
          </label>
          <select
            id="admin-gigs-filter-type"
            value={contentType}
            onChange={(event) => onContentTypeChange(event.target.value as "GIGS" | "BUSINESS_LISTINGS")}
            className="h-[50px] rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value="GIGS">Gigs</option>
            <option value="BUSINESS_LISTINGS">Business Listings</option>
          </select>
        </div>

        <div className="min-w-[280px] flex-[1.8_1_320px]">
          <label htmlFor="admin-gigs-filter-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Search
          </label>
          <input
            id="admin-gigs-filter-search"
            type="text"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Title, category, city, state, zip"
            className="w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-base text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div>

        <div className="min-w-[220px] flex-[1.1_1_240px]">
          <label htmlFor="admin-gigs-filter-poster" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Poster
          </label>
          <input
            id="admin-gigs-filter-poster"
            type="text"
            value={poster}
            onChange={(event) => onPosterChange(event.target.value)}
            placeholder="Email or name"
            className="w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-base text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div>

        <div className="min-w-[140px] flex-[1_1_160px]">
          <label htmlFor="admin-gigs-filter-status" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Status
          </label>
          <select
            id="admin-gigs-filter-status"
            value={status}
            onChange={(event) => onStatusChange(event.target.value)}
            className="h-[50px] rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value="">All statuses</option>
            <option value="DRAFT">DRAFT</option>
            <option value="PUBLISHED">PUBLISHED</option>
            {contentType === "GIGS" ? <option value="FILLED">FILLED</option> : null}
            {contentType === "GIGS" ? <option value="CANCELLED">CANCELLED</option> : null}
            {contentType === "BUSINESS_LISTINGS" ? <option value="EXPIRED">EXPIRED</option> : null}
            {contentType === "BUSINESS_LISTINGS" ? <option value="CLOSED">CLOSED</option> : null}
          </select>
        </div>

        <div className="min-w-[160px] flex-[1_1_180px]">
          <label htmlFor="admin-gigs-filter-created-from" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Created From
          </label>
          <input
            id="admin-gigs-filter-created-from"
            type="date"
            value={createdFromDate}
            onChange={(event) => onCreatedFromDateChange(event.target.value)}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div>

        <div className="min-w-[160px] flex-[1_1_180px]">
          <label htmlFor="admin-gigs-filter-created-to" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Created To
          </label>
          <input
            id="admin-gigs-filter-created-to"
            type="date"
            min={createdFromDate || undefined}
            value={createdToDate}
            onChange={(event) => onCreatedToDateChange(event.target.value)}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div>

        {contentType === "GIGS" ? <div className="min-w-[160px] flex-[1_1_180px]">
          <label htmlFor="admin-gigs-filter-start-from" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Start From
          </label>
          <input
            id="admin-gigs-filter-start-from"
            type="date"
            value={startFromDate}
            onChange={(event) => onStartFromDateChange(event.target.value)}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div> : null}

        {contentType === "GIGS" ? <div className="min-w-[160px] flex-[1_1_180px]">
          <label htmlFor="admin-gigs-filter-start-to" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Start To
          </label>
          <input
            id="admin-gigs-filter-start-to"
            type="date"
            min={startFromDate || undefined}
            value={startToDate}
            onChange={(event) => onStartToDateChange(event.target.value)}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div> : null}

        <div className="min-w-[110px] flex-[0_1_120px]">
          <label htmlFor="admin-gigs-filter-page-size" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Rows
          </label>
          <select
            id="admin-gigs-filter-page-size"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            className="h-[50px] rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
        </div>

        <div className="min-w-[180px] flex-[0_1_220px]">
          <Button type="button" variant="outline" className="h-[50px] w-full justify-center" onClick={onRequestTotalCount}>
            Get total count
          </Button>
        </div>
      </div>
    </AdminTableFiltersCard>
  );
};

export default AdminGigsFilters;
